import { ArrowDownTrayIcon, PhotoIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { isEmpty, isFunction, isString } from "lodash";
import NoImage from "../../assets/images/no_image_icon.png";
import useToast from "../../hooks/useToast";
import { ToastTypes } from "../../context/ToastProvider";

/**
 * 항목을 성격별로 묶어 보여준다.
 * 28개를 한 줄씩 나열하면 찾기 어려워서, 조회 목적(기본 확인 / 판정 / 색상 분석)에 맞춰 그룹화했다.
 */
const FIELD_GROUPS = [
    {
        title: "기본 정보",
        fields: [
            { name: "일련번호", id: "test_seq_num" },
            { name: "항목", id: "spec_nm" },
            { name: "검사일자", id: "inst_dt" },
            { name: "계정명", id: "user_nm" },
            { name: "그룹명", id: "group_user_nm" },
            { name: "촬영 환경", id: "description" },
        ],
    },
    {
        title: "디바이스 · 실행",
        fields: [
            { name: "테스트 디바이스", id: "info_device_model" },
            { name: "가입된 디바이스", id: "device_model" },
            { name: "밝기", id: "brightness" },
            { name: "openCV 실행", id: "exec_opencv" },
            { name: "AI 실행", id: "exec_ai" },
        ],
    },
    {
        title: "색상 측정값",
        fields: [
            { name: "측정된 RGB", id: "insp_rgb" },
            { name: "보정된 RGB", id: "gray_scaled_rgb" },
            { name: "Lab", id: "lab" },
            { name: "Y(G)", id: "yg" },
            { name: "Cb", id: "cb" },
            { name: "Cr", id: "cr" },
            { name: "Cb-Cr", id: "distanceCbCr" },
            { name: "RGB거리", id: "distanceRGB" },
        ],
    },
    {
        title: "색상 변환값",
        fields: [
            { name: "C", id: "c" },
            { name: "M", id: "m" },
            { name: "Y", id: "y" },
            { name: "K-상수", id: "kval" },
            { name: "K-소수", id: "kconstant" },
            { name: "HSL", id: "h" },
            { name: "델타", id: "deltaPrime" },
        ],
    },
    {
        title: "파일",
        fields: [{ name: "파일 위치", id: "file_path" }],
    },
];

export default function ImagePreviewUrinalysis({ images, zipFile, imgPath, imgSource, data, masked = true, onClose, ...props }) {
    const { showToast } = useToast();

    const hasImage = imgSource && !isEmpty(imgSource);

    const handleOnClose = () => {
        if (onClose && isFunction(onClose)) {
            onClose();
        }
    };

    const handleOnClickDownloadImage = () => {
        const link = document.createElement("a");
        link.href = imgSource;
        link.download = imgPath;
        document.body.appendChild(link);
        link.click();
        link.remove();
        showToast(ToastTypes.SUCCESS, "성공", "이미지를 성공적으로 다운로드했습니다.");
    };

    const rgbToCssColor = (inspRgb) => {
        if (inspRgb && isString(inspRgb)) {
            const splittedRGB = inspRgb.split(",");
            return `rgb(${splittedRGB[0]}, ${splittedRGB[1]}, ${splittedRGB[2]})`;
        }
        return "transparent";
    };

    const getDeviceModel = (deviceModel) => {
        if (deviceModel && isString(deviceModel) && !isEmpty(deviceModel)) {
            return deviceModel.includes("iPhone") ? "iPhone" : "Android";
        }
        return "";
    };

    const maskedUserNm = (name) => {
        if (!isString(name)) return "";
        // 표의 "마스킹 처리" 토글을 그대로 따른다.
        if (!masked) return name;
        if (name.length <= 2) return name;
        const firstChar = name.charAt(0);
        const lastChar = name.charAt(name.length - 1);
        return `${firstChar}${"*".repeat(name.length - 2)}${lastChar}`;
    };

    const renderFieldValue = (item) => {
        const raw = data?.[item.id];

        if (item.id.includes("rgb")) {
            return (
                <div className="flex items-center gap-x-2">
                    <span className="tabular-nums">{raw || "-"}</span>
                    {raw && (
                        <span
                            className="h-5 w-5 shrink-0 rounded ring-1 ring-inset ring-black/10"
                            style={{ backgroundColor: rgbToCssColor(raw) }}
                        />
                    )}
                </div>
            );
        }
        if (item.id.includes("device_model")) return getDeviceModel(raw) || "-";
        // user_nm = 계정(대표자) 이름, group_user_nm = 검사받은 그룹원 이름. 둘 다 마스킹한다.
        if (item.id === "user_nm" || item.id === "group_user_nm") return maskedUserNm(raw) || "-";
        if (item.id === "file_path") return <span className="break-all">{imgPath || "-"}</span>;

        return raw === null || raw === undefined || raw === "" ? "-" : String(raw);
    };

    return (
        <div className="flex h-screen min-h-0 flex-col bg-slate-50">
            {/* 헤더 */}
            <div className="flex shrink-0 items-center justify-between gap-x-4 border-b border-slate-200 bg-white px-5 py-3">
                <div className="flex min-w-0 items-center gap-x-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-50">
                        <PhotoIcon className="h-5 w-5 text-emerald-600" aria-hidden="true" />
                    </span>
                    <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-900">모디엠 소변검사</div>
                        <div className="truncate text-xs text-slate-500">{data?.spec_nm || "이미지 상세"}</div>
                    </div>
                </div>

                <div className="flex shrink-0 items-center gap-x-2">
                    {hasImage && (
                        <button
                            type="button"
                            className="motion-press inline-flex items-center gap-x-1.5 rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500"
                            onClick={() => handleOnClickDownloadImage()}
                        >
                            <ArrowDownTrayIcon className="h-4 w-4" aria-hidden="true" />
                            이미지 다운로드
                        </button>
                    )}
                    <button
                        type="button"
                        className="motion-press inline-flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-slate-300 hover:bg-slate-50"
                        onClick={() => handleOnClose()}
                    >
                        <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                        닫기
                    </button>
                </div>
            </div>

            {/* 본문: 좌 이미지 / 우 상세 */}
            <div className="flex min-h-0 flex-1 gap-4 p-4">
                {/* 이미지 */}
                <div className="flex min-h-0 w-[45%] shrink-0 flex-col gap-3">
                    <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-xl bg-white p-3 shadow-sm ring-1 ring-slate-200">
                        {hasImage ? (
                            <img
                                src={imgSource}
                                alt="검사 이미지"
                                className="max-h-full max-w-full rounded-lg object-contain"
                            />
                        ) : (
                            <div className="flex flex-col items-center gap-y-2 text-slate-400">
                                <img src={NoImage} alt="" className="h-24 w-24 opacity-60" />
                                <span className="text-sm">이미지가 없습니다</span>
                            </div>
                        )}
                    </div>

                    {/* 판정 결과 — 가장 중요한 값이라 이미지 바로 아래 크게 */}
                    <div className="grid shrink-0 grid-cols-2 gap-3">
                        <div className="rounded-xl bg-white px-4 py-3 shadow-sm ring-1 ring-slate-200">
                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">모바일 단계</div>
                            <div className="mt-1 text-xl font-bold tabular-nums text-gray-900">
                                {data?.insp_level ?? "-"}
                            </div>
                        </div>
                        <div className="rounded-xl bg-white px-4 py-3 shadow-sm ring-1 ring-slate-200">
                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">검증 단계</div>
                            <div className="mt-1 text-xl font-bold tabular-nums text-blue-600">
                                {data?.calculate_lvl ?? "-"}
                            </div>
                        </div>
                    </div>
                </div>

                {/* 상세 정보 */}
                <div className="thin-scrollbar min-h-0 flex-1 overflow-y-auto rounded-xl bg-white shadow-sm ring-1 ring-slate-200">
                    {FIELD_GROUPS.map((group) => (
                        <div key={group.title} className="border-b border-slate-100 last:border-b-0">
                            <div className="sticky top-0 z-10 border-b border-slate-200 bg-gradient-to-b from-slate-50 to-slate-100 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                                {group.title}
                            </div>
                            <dl className="divide-y divide-slate-100">
                                {group.fields.map((item) => (
                                    <div key={item.id} className="flex items-center gap-x-3 px-4 py-2">
                                        <dt className="w-32 shrink-0 text-xs font-semibold text-slate-500">{item.name}</dt>
                                        <dd className="min-w-0 flex-1 text-xs text-gray-900">{renderFieldValue(item)}</dd>
                                    </div>
                                ))}
                            </dl>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
